import React from 'react'
import { Box, Typography, Button, Divider, Stack } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded'
import { motion } from 'framer-motion'
import { signupPalette } from './signupTheme'

// Celebration particles (framer-motion only — no extra dependencies)
const PARTICLES = [
  { x: -120, y: -70, color: signupPalette.third.main, size: 10, delay: 0.15 },
  { x: 110, y: -90, color: '#F4B860', size: 8, delay: 0.2 },
  { x: -70, y: -120, color: '#E4572E', size: 7, delay: 0.28 },
  { x: 140, y: -30, color: signupPalette.third.light, size: 9, delay: 0.33 },
  { x: -150, y: -20, color: '#8BC34A', size: 8, delay: 0.4 },
  { x: 60, y: -130, color: signupPalette.primary.main, size: 7, delay: 0.45 },
  { x: -40, y: -95, color: '#F4B860', size: 6, delay: 0.5 },
  { x: 95, y: -115, color: '#8BC34A', size: 6, delay: 0.55 },
]

const TenantReadyMessage = ({ url }) => {
  return (
    <Box
      sx={{
        position: 'relative',
        background:
          'linear-gradient(160deg, rgba(35,154,164,0.08) 0%, #ffffff 55%)',
        border: '1px solid rgba(35,154,164,0.25)',
        borderRadius: 4,
        p: { xs: 3, md: 4 },
        textAlign: 'center',
        maxWidth: 520,
        mx: 'auto',
        overflow: 'hidden',
      }}
    >
      {/* celebration burst */}
      <Box
        sx={{
          position: 'absolute',
          top: '38%',
          left: '50%',
          width: 0,
          height: 0,
          pointerEvents: 'none',
        }}
      >
        {PARTICLES.map((p, i) => (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, scale: 0 }}
            animate={{ x: p.x, y: p.y, opacity: 0, scale: 1 }}
            transition={{ duration: 1.3, delay: p.delay, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: p.size,
              height: p.size,
              borderRadius: i % 2 ? 2 : '50%',
              backgroundColor: p.color,
              display: 'block',
            }}
          />
        ))}
      </Box>

      <motion.div
        initial={{ scale: 0, rotate: -30 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 260, damping: 14, delay: 0.1 }}
        style={{ display: 'inline-flex' }}
      >
        <Box
          sx={{
            width: 84,
            height: 84,
            borderRadius: '50%',
            bgcolor: 'rgba(35,154,164,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <CheckCircleRoundedIcon
            sx={{ fontSize: 56, color: signupPalette.third.main }}
          />
        </Box>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.25 }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: signupPalette.primary.main,
            letterSpacing: '-0.02em',
            mb: 1,
          }}
        >
          You&apos;re all set!
        </Typography>

        <Typography sx={{ color: 'text.secondary', mb: 1 }}>
          Payment confirmed and your <strong>AmbuNet&reg;</strong> system is
          ready to go.
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" color="text.secondary">
            Sign in with the email address and password you chose at signup.
          </Typography>
        </Stack>

        <Divider sx={{ mb: 3 }} />

        <Button
          href={url}
          variant="contained"
          size="large"
          startIcon={<RocketLaunchRoundedIcon />}
          sx={{
            bgcolor: signupPalette.third.main,
            color: '#fff',
            '&:hover': { bgcolor: signupPalette.third.dark },
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 700,
            px: 4,
            py: 1.5,
            borderRadius: 2,
          }}
        >
          Launch AmbuNet
        </Button>
      </motion.div>
    </Box>
  )
}

export default TenantReadyMessage
